import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

/** The caller's own pinned opportunities (server-stored, per GroundZero user). A pin names one
 *  specific contract pair by its `opportunity_id`, so Near+Mid and Mid+Far are separate pins. */
export function useOpportunityPins() {
  const [pins, setPins] = useState([]);
  const [error, setError] = useState(null);

  const reload = useCallback(() => api.get("/paper/v25/opportunity-pins")
    .then((r) => { setPins(r.data?.pins || []); setError(null); })
    .catch(() => setError("Could not load pinned opportunities")), []);

  useEffect(() => { reload(); }, [reload]);

  const pin = useCallback(async (market, row) => {
    const r = await api.post("/paper/v25/opportunity-pins", { market, row });
    setPins((prev) => (prev.some((p) => p.id === r.data.id) ? prev : [...prev, r.data]));
  }, []);

  const unpin = useCallback(async (pinId) => {
    await api.delete(`/paper/v25/opportunity-pins/${pinId}`);
    setPins((prev) => prev.filter((p) => p.id !== pinId));
  }, []);

  const pinFor = useCallback((opportunityId) => pins.find((p) => p.opportunity_id === opportunityId) || null,
    [pins]);

  return { pins, pin, unpin, pinFor, error, reload };
}

/** One pinned row's display state from the latest poll.
 *  `rows` is the endpoint's FULL row list (stale rows included), `memory` the last-known
 *  {row, at} per opportunity id kept across polls. Returns {row, status, at, live}. */
export function resolvePinned(pin, rows, memory) {
  const live = (rows || []).find((r) => r.opportunity_id === pin.opportunity_id) || null;
  if (live && live.market_data_status === "OK") {
    return { row: live, status: "OK", at: null, live: true };
  }
  const known = memory?.[pin.opportunity_id];
  const row = known?.row || (live ? { ...pin.snapshot, ...live } : pin.snapshot) || {};
  const at = known?.at || pin.snapshot_at || pin.pinned_at || null;
  const status = live ? (live.market_data_status || "STALE") : "NOT_IN_FEED";
  return { row, status, at, live: false };
}
